import { useEffect, useRef, useState } from 'react';

import clsx from 'clsx';

import { useTranslation } from '@crehana/i18n';
import { useIntersectionObserver } from '@crehana/react-hooks';
import { Col, Container, Row } from '@crehana/ui';

import { createImgixPlaceholder, CustomNextImage } from '@crehana/next';

import { WHY_CREHANA_CARDS } from '../constants';

import { StyledImage, StyledLeftBar } from './WhyCrehanaMobile.styles';

const WhyCrehanaMobile = () => {
  const { t } = useTranslation();

  const [isTitleSticky, setIsTitleSticky] = useState(false);
  const [cardInView, setCardInView] = useState<number>(-1);
  const setTitleStickyRef = useRef<HTMLDivElement>(null);
  const unsetTitleStickyRef = useRef<HTMLDivElement>(null);
  const cardsRef = [
    useRef<HTMLDivElement>(null),
    useRef<HTMLDivElement>(null),
    useRef<HTMLDivElement>(null),
    useRef<HTMLDivElement>(null),
  ];
  const firstCardObserver = useIntersectionObserver(cardsRef[0], {});
  const secondCardObserver = useIntersectionObserver(cardsRef[1], {});
  const thirdCardObserver = useIntersectionObserver(cardsRef[2], {});
  const fourthCardObserver = useIntersectionObserver(cardsRef[3], {});
  const titleObserver = useIntersectionObserver(setTitleStickyRef, {});
  const bottomObserver = useIntersectionObserver(unsetTitleStickyRef, {});

  useEffect(() => {
    if (
      // That means that the element is intersecting at the top of viewport
      titleObserver?.intersectionRatio === 0 &&
      titleObserver?.boundingClientRect.top < 0
    ) {
      setIsTitleSticky(true);
    } else {
      setIsTitleSticky(false);
    }
  }, [titleObserver?.isIntersecting]);

  useEffect(() => {
    if (
      // That means that the element is intersecting at the top of viewport
      bottomObserver?.intersectionRatio === 0 &&
      bottomObserver?.boundingClientRect.top < 0 &&
      isTitleSticky === true
    ) {
      setIsTitleSticky(false);
    }
  }, [bottomObserver?.isIntersecting]);

  useEffect(() => {
    if (
      // That means that the element is intersecting at the top of viewport
      firstCardObserver?.intersectionRatio === 0 &&
      firstCardObserver?.boundingClientRect.top < 0
    ) {
      setCardInView(0);
    }
    if (
      // That means that the element is intersecting at the top of viewport
      secondCardObserver?.intersectionRatio === 0 &&
      secondCardObserver?.boundingClientRect.top < 0
    ) {
      setCardInView(1);
    }
    if (
      // That means that the element is intersecting at the top of viewport
      thirdCardObserver?.intersectionRatio === 0 &&
      thirdCardObserver?.boundingClientRect.top < 0
    ) {
      setCardInView(2);
    }
    if (
      // That means that the element is intersecting at the top of viewport
      fourthCardObserver?.intersectionRatio === 0 &&
      fourthCardObserver?.boundingClientRect.top < 0
    ) {
      setCardInView(3);
    }
  }, [
    firstCardObserver?.isIntersecting,
    secondCardObserver?.isIntersecting,
    thirdCardObserver?.isIntersecting,
    fourthCardObserver?.isIntersecting,
  ]);

  return (
    <section className="relative">
      <Container>
        <Row>
          <Col className="relative flex justify-center mt-52">
            <div className="absolute top-0" ref={setTitleStickyRef} />
            <h2
              className={clsx(
                'text-20 font-gil font-bold text-primary-light uppercase bg-white w-full text-center pt-24 pb-24 top-0 z-4',
                { fixed: isTitleSticky },
              )}
            >
              {t('WHY_CREHANA_TITLE')}
            </h2>
          </Col>
        </Row>
        <Row className={clsx({ 'mt-76 sm:mt-80 md:pt-96': isTitleSticky })}>
          <Col style={{ maxWidth: 560 }} className="mx-auto">
            {WHY_CREHANA_CARDS.map((card, i) => (
              <div key={`why-crehana-card-${i}`} className="mb-96 relative">
                <div
                  ref={cardsRef[i]}
                  className="absolute"
                  style={{ top: '-75vh' }}
                />
                <div className="flex flex-row">
                  <StyledLeftBar
                    index={i}
                    accentColor={card.accentColor}
                    prevColor={
                      cardInView > i
                        ? WHY_CREHANA_CARDS[i - 1]?.accentColor ||
                          WHY_CREHANA_CARDS[0].accentColor
                        : WHY_CREHANA_CARDS[i].accentColor
                    }
                    cardInView={cardInView}
                    className="relative overflow-hidden w-8 h-auto ml-16 mr-24 flex-shrink-0 flex-grow-0"
                  />
                  <div className="flex flex-col py-8">
                    <h3
                      className="font-gil font-bold leading-30"
                      style={{ fontSize: '28px' }}
                    >
                      {t(card.title)}
                    </h3>
                    <p className="font-nun text-18 leading-24 mt-32">
                      {t(card.description)}
                    </p>
                  </div>
                </div>
                <StyledImage className="relative mx-auto mt-40">
                  <CustomNextImage
                    src={card.image}
                    blurDataURL={createImgixPlaceholder(card.image)}
                    layout="fill"
                    alt="Hero background"
                    objectFit="contain"
                    objectPosition="inherit"
                    placeholder="blur"
                  />
                </StyledImage>
              </div>
            ))}
          </Col>
        </Row>
      </Container>
      <div className="absolute bottom-0" ref={unsetTitleStickyRef} />
    </section>
  );
};

export default WhyCrehanaMobile;
